import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { Modal as CosmaModal } from 'is24-corecss';
import { css } from '@emotion/css';
import { Tabs } from '../tabs/Tabs';
import type { TabsProps } from '../tabs/Tabs';
import type { Benefit } from '../../../../@types/benefits';
import type { WebPImageElement } from '../imageSource/ImageSource';
import { ImageSource } from '../imageSource/ImageSource';
import './Modal.css';
import { modalStyles as styles } from './modalStyles';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

interface ModalV2Props {
  title: string | ReactNode;
  isVisible: boolean;
  image: string;
  imageWebp?: WebPImageElement;
  imageMobile?: string;
  imageMobileWebp?: WebPImageElement;
  imageUseFullWidth: boolean;
  imageAlt: string;
  imageBackgroundStyles?: object;
  tabsContent: TabsProps['tabsContent'];
  primaryFooterBtn?: ReactNode;
  secondaryFooterBtn?: ReactNode;
  formData?: ReactElement | null;
  compact?: boolean;
  benefit: Benefit;
  onClose: () => void;
}

const ModalV2 = ({
  title,
  isVisible,
  image,
  imageWebp,
  imageMobile,
  imageMobileWebp,
  imageUseFullWidth,
  imageAlt,
  imageBackgroundStyles,
  tabsContent,
  benefit,
  formData,
  compact,
  primaryFooterBtn,
  secondaryFooterBtn,
  onClose,
}: ModalV2Props) => {
  const titleId = React.useId();
  const camelCasedContentIdentifier = `_${benefit.toLowerCase()}ModalContent`.replace(/_./g, (match) =>
    match[1].toUpperCase(),
  );
  useElementDarkMode('[data-testid="modal-backdrop"]', isVisible);
  useBodyScroll(isVisible);
  useModalFocus(isVisible);

  return (
    <CosmaModal.Root isOpen={isVisible} onClose={onClose} fullHeight={compact}>
      <CosmaModal.Backdrop className={compact ? 'ModalV2-compact-backdrop display-flex flex-column' : undefined}>
        <CosmaModal.Surface
          aria-labelledby={titleId}
          className={`ModalV2 one-whole display-flex ${compact ? 'ModalV2-compact' : 'overflow-hidden'} ${css(styles.container)} ${camelCasedContentIdentifier}`}
        >
          <CosmaModal.Header className={styles.fixedSection}>
            <CosmaModal.Title id={titleId}>{title}</CosmaModal.Title>
            <CosmaModal.CloseButton />
          </CosmaModal.Header>
          <CosmaModal.Body className={`ModalV2-body padding-none ${styles.modalContent}`}>
            {!formData && (
              <div
                data-testid='ImageWrapper'
                className={`display-flex overflow-hidden border-bottom margin-bottom-xs palm-margin-bottom-s ${css({ ...styles.imageWrapper, ...imageBackgroundStyles })}`}
              >
                <ImageSource
                  image={image}
                  imageWebp={imageWebp}
                  imageMobile={imageMobile}
                  imageMobileWebp={imageMobileWebp}
                  imageAlt={imageAlt}
                  sizes='(min-width: 1014px) 1000px, 100vw'
                  pictureClassName='display-flex one-whole height-full'
                  className='display-block one-whole height-full'
                  style={{ objectFit: imageUseFullWidth ? 'cover' : 'contain' }}
                />
              </div>
            )}
            <div className='absolute-reference padding-horizontal-l palm-padding-horizontal-xl'>
              {formData ? (
                formData
              ) : (
                <Tabs
                  tabsContent={tabsContent}
                  benefit={benefit}
                  tabListClassName='grid-justify-center margin-bottom-none padding-horizontal-none padding-top-m palm-padding-top-l padding-bottom-l'
                />
              )}
            </div>
          </CosmaModal.Body>
          {(primaryFooterBtn || secondaryFooterBtn) && (
            <CosmaModal.Footer className={styles.fixedSection}>
              {secondaryFooterBtn}
              {primaryFooterBtn}
            </CosmaModal.Footer>
          )}
        </CosmaModal.Surface>
      </CosmaModal.Backdrop>
    </CosmaModal.Root>
  );
};

export default ModalV2;
